import * as yup from "yup";

import { AccountantService, AuthService } from "../../services";
import {
	BackdropCircularProgress,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { scrollPanelFieldsToTop, useNavigateToLogout } from "../../common/Common";
import { setSelectedAccountant, setSelectedAccountantName, setSelectedClient, signInUpdate } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";

import { IAppState } from "../../store/reducers";
import {
	INITIAL_SNACKBAR_DATA,
} from "../../common/constants";
import { PADAgreenmentAccountDetailsPanel } from "./Panels/PADAgreenmentAccountDetailsPanel";
import { PADAgreenmentAccountHolderDetailsFieldsPanel } from "./Panels/PADAgreenmentAccountHolderDetailsFieldsPanel";
import { PADAgreenmentBankAccountSettingsFieldsPanel } from "./Panels/PADAgreenmentBankAccountSettingsFieldsPanel";
import { PADAgreenmentBankFieldsPanel } from "./Panels/PADAgreenmentBankFieldsPanel";
import { PADAgreenmentBankManagerFieldsPanel } from "./Panels/PADAgreenmentBankManagerFieldsPanel";
import { PADAgreenmentJointAccountHolderFieldsPanel } from "./Panels/PADAgreenmentJointAccountHolderFieldsPanel";
import { PADAgreenmentToolbar } from "./PADAgreenmentToolbar";
import { PADAgreenmentValidationSchema } from "./validator";
import { isEmpty } from "../AccountOrBankDetails/validator";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IPADAgreenment { }
const useGetData = (parentId: string | null, bankId: string | null) =>
	useQuery({
		queryKey: ["pad-agreement-data", parentId, bankId],
		queryFn: () => {
			return AuthService.getPADAgreenmentBankDetails(parentId, bankId);
		},
		refetchOnWindowFocus: false,
	});
export const PADAgreenment: React.FC<IPADAgreenment> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isPendingData, setIsPendingData] = useState(false);

	const [isJointAccount, setIsJointAccount] = useState(100);
	const [openDialog, setOpenDialog] = useState(false);
	const userIdStore = useSelector((state: IAppState) => state.user.profile.userId);
	const onCloseHandler = () => {
		setOpenDialog(false)
	}
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const navigateToLogout = useNavigateToLogout();
	const lastBankScreen = useSelector(
		(state: IAppState) => state.user.profile.lastBankScreen
	);
	const [parentId, setParentId] = useState<any>(null)
	const [accountantBankDetailsForPADData, setAccountantBankDetailsForPADData] = useState<any>(undefined)

	const { isPending, data: padAgreementBankDetails }: any = useGetData(
		accountantBankDetailsForPADData && accountantBankDetailsForPADData.parentId,
		accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankId
	);

	useEffect(() => {
		if (padAgreementBankDetails) {
			Object.keys(padAgreementBankDetails).forEach((fieldName: any) => {
				const fieldValue = padAgreementBankDetails[fieldName];
				formMethods.setValue(fieldName, fieldValue)
				setIsJointAccount(padAgreementBankDetails.isJointAccount)
			})
		}
	}, [padAgreementBankDetails])



	useEffect(() => {
		if (lastBankScreen !== 300 && isAdminUser === 100) {
			setIsPendingData(true);
			(async () => {
				try {
					const accountantResponse: any =
						await AccountantService.getAccountants({
							search_query: "",
							order_by: "acc_title",
							order_direction: "asc",
							page: 1,
							size: 25,
						}, isAdminUser);
					if (
						accountantResponse &&
						accountantResponse.items.length === 1
					) {
						dispatch(setSelectedAccountant(accountantResponse.items[0]));
						dispatch(setSelectedAccountantName(accountantResponse.items[0]));
						setParentId(accountantResponse.items[0].id);
						(async () => {
							try {
								const accountantBankDetailsForPAD: any =
									await AccountantService.getAccountantBankDetailsForPAD(
										accountantResponse.items[0].id,
										isAdminUser
									);
								if (accountantBankDetailsForPAD) {
									setAccountantBankDetailsForPADData(accountantBankDetailsForPAD)
									formMethods.setValue('bankName', accountantBankDetailsForPAD.bankName)
									formMethods.setValue('branchAddress', accountantBankDetailsForPAD.branchAddress)
								}
							} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
						})();
					}
				} catch { setIsPendingData(false) }
			})();
		} else if (lastBankScreen !== 300 && isAdminUser === 200) {
			setIsPendingData(true);
			(async () => {
				try {
					const clientResponse: any =
						await AccountantService.getClientByAccountantId(
							"",
							isAdminUser,
							{
								search_query: "",
								order_by: "client_name",
								order_direction: "asc",
								page: 1,
								size: 25,
							}, navigateToLogout);
					if (
						clientResponse &&
						clientResponse.items.length === 1
					) {
						dispatch(setSelectedClient(clientResponse.items[0]));
						setParentId(clientResponse.items[0].id);
						(async () => {
							try {
								const accountantBankDetailsForPAD: any =
									await AccountantService.getAccountantBankDetailsForPAD(
										clientResponse.items[0].id,
										isAdminUser
									);
								if (accountantBankDetailsForPAD) {
									setAccountantBankDetailsForPADData(accountantBankDetailsForPAD)
									formMethods.setValue('bankName', accountantBankDetailsForPAD.bankName)
									formMethods.setValue('branchAddress', accountantBankDetailsForPAD.branchAddress)
								}
							} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
						})();
					}
				} catch { setIsPendingData(false) }
			})();
		}
	}, [lastBankScreen])

	const { isPending: isSubmitting, mutate: savePADAgreenmentFieldsData } =
		useMutation({
			mutationFn: AuthService.sendForVerificationPADAgreenment,
			onSuccess: (res: IServerError | IResponse | any) => {
				if (res.status === 200 || res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					// if (lastBankScreen && lastBankScreen !== 300) {
					// 	(async () => {
					// 		try {
					// 			setIsPendingData(true)
					// 			const response = await AuthService.lastBankScreen(userIdStore, 200);  //Upload documents
					// 			dispatch(
					// 				signInUpdate({
					// 					user: { lastBankScreen: 200 },
					// 				})
					// 			);
					// 		} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
					// 	})();
					// } else {
					// 	// let path = `/upload-documents`;
					// 	// navigate(path);
					// }
					(async () => {
						try {
							setIsPendingData(true)
							const accountantBankDetailsForPAD: any =
								await AccountantService.getAccountantBankDetailsForPAD(
									parentId,
									isAdminUser
								);
							if (accountantBankDetailsForPAD) {
								setAccountantBankDetailsForPADData(accountantBankDetailsForPAD)
								formMethods.setValue('bankName', accountantBankDetailsForPAD.bankName)
								formMethods.setValue('branchAddress', accountantBankDetailsForPAD.branchAddress)
							}
						} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
					})();
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const DEFAULT_VALUES = {
		bankAccountNumber: undefined,
		bankTransitNumber: undefined,
		bankfinancialInstitutionNumber: undefined,
		bankName: undefined,
		branchAddress: undefined,
		accountType: undefined,
		theseServicesAreFor: undefined,
		bankManagerName: undefined,
		bankManagerEmail: undefined,
		bankManagerPhone: undefined,
		bankManagerExt: undefined,
		isJointAccount: undefined,
		accountHolderName: undefined,
		accountHolderEmail: undefined,
		accountHolderPhone: undefined,
		accountHolderExt: undefined,
		jointAccountHolderName: undefined,
		jointAccountHolderEmail: undefined,
		jointAccountHolderPhone: undefined,
		jointAccountHolderExt: undefined,
	};

	let method = "websential.ca"
	const ExtendedValidationSchema = useMemo(() =>
		PADAgreenmentValidationSchema.shape({
			bankManagerEmail: yup
				.string()
				.nullable()
				.test(
					"transaction-alert-email",
					"Invalid email format",
					function (value) {
						if (isEmpty(value)) return true;

						const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
						if (!emailRegex.test(value!)) {
							return this.createError({ message: "Must be a valid email address" });
						}

						if (!value!.endsWith(`@${accountantBankDetailsForPADData?.bankDomain || "simplepay.ca"}`)) {
							return this.createError({
								message: `Email must use @${accountantBankDetailsForPADData?.bankDomain} domain (e.g. example@${accountantBankDetailsForPADData?.bankDomain})`,
							});
						}

						return true;
					}
				),
			jointAccountHolderName: yup
				.string()
				.nullable()
				.test(
					"joint-account-holder-name",
					"Name is required",
					function (value) {
						const { isJointAccount } = this.parent;
						console.log("isJointAccount", isJointAccount)
						if (isJointAccount === "200") {
							return !!value?.trim() || this.createError({
								message: "Name is required",
							});
						}

						return true;
					}
				),

			jointAccountHolderEmail: yup
				.string()
				.nullable()
				.test(
					"joint-account-holder-email",
					"Email is required",
					function (value) {
						const { isJointAccount } = this.parent;

						if (isJointAccount === "200") {
							return !!value?.trim() || this.createError({
								message: "Email is required",
							});
						}

						return true;
					}
				),

			jointAccountHolderPhone: yup
				.string()
				.nullable()
				.test(
					"joint-account-holder-phone",
					"Phone No. is required",
					function (value) {
						const { isJointAccount } = this.parent;

						if (isJointAccount === "200") {
							return !!value?.trim() || this.createError({
								message: "Phone No. is required",
							});
						}

						return true;
					}
				),
		}),
		[accountantBankDetailsForPADData]);

	const formMethods = useForm<any>({
		resolver: yupResolver(ExtendedValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {
		console.log('openDialog', openDialog)
		if (openDialog) {
			setOpenDialog(false)
			let payload = {
				companyName: accountantBankDetailsForPADData && accountantBankDetailsForPADData.accTitle,
				parentId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.parentId,
				bankId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankId,
				parentScreen: isAdminUser,
				bankAccountNumber: accountantBankDetailsForPADData && accountantBankDetailsForPADData.accountNo,
				bankTransitNumber: accountantBankDetailsForPADData && accountantBankDetailsForPADData.branchTransitNumber,
				bankfinancialInstitutionNumber: accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankFinancialNo,
				bankName: values.bankName,
				branchAddress: values.branchAddress,
				accountType: Number(values.accountType),
				theseServicesAreFor: Number(values.theseServicesAreFor),
				bankManagerName: values.bankManagerName,
				bankManagerEmail: values.bankManagerEmail,
				bankManagerPhone: values.bankManagerPhone,
				bankManagerExt: values.bankManagerExt,
				isJointAccount: Number(values.isJointAccount),
				accountHolderName: values.accountHolderName,
				accountHolderEmail: values.accountHolderEmail,
				accountHolderPhone: values.accountHolderPhone,
				accountHolderExt: values.accountHolderExt,
				jointAccountHolderName: values.jointAccountHolderName,
				jointAccountHolderEmail: values.jointAccountHolderEmail,
				jointAccountHolderPhone: values.jointAccountHolderPhone,
				jointAccountHolderExt: values.jointAccountHolderExt,
			};
			savePADAgreenmentFieldsData(payload);
		} else {
			setOpenDialog(true)
		}
	};

	const onSaveButtonClickHandler = () => {
		formMethods.clearErrors();
		const [
			bankAccountNumber,
			bankTransitNumber,
			bankfinancialInstitutionNumber,
			bankName,
			branchAddress,
			accountType,
			theseServicesAreFor,
			bankManagerName,
			bankManagerEmail,
			bankManagerPhone,
			bankManagerExt,
			isJointAccount,
			accountHolderName,
			accountHolderEmail,
			accountHolderPhone,
			accountHolderExt,
			jointAccountHolderName,
			jointAccountHolderEmail,
			jointAccountHolderPhone,
			jointAccountHolderExt,
		] = formMethods.watch([
			"bankAccountNumber",
			"bankTransitNumber",
			"bankfinancialInstitutionNumber",
			"bankName",
			"branchAddress",
			"accountType",
			"theseServicesAreFor",
			"bankManagerName",
			"bankManagerEmail",
			"bankManagerPhone",
			"bankManagerExt",
			"isJointAccount",
			"accountHolderName",
			"accountHolderEmail",
			"accountHolderPhone",
			"accountHolderExt",
			"jointAccountHolderName",
			"jointAccountHolderEmail",
			"jointAccountHolderPhone",
			"jointAccountHolderExt",
		]);
		let payload = {
			companyName: accountantBankDetailsForPADData && accountantBankDetailsForPADData.accTitle,
			parentId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.parentId,
			bankId: accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankId,
			parentScreen: isAdminUser,
			bankAccountNumber: accountantBankDetailsForPADData && accountantBankDetailsForPADData.accountNo,
			bankTransitNumber: accountantBankDetailsForPADData && accountantBankDetailsForPADData.branchTransitNumber,
			bankfinancialInstitutionNumber: accountantBankDetailsForPADData && accountantBankDetailsForPADData.bankFinancialNo,
			bankName: bankName,
			branchAddress: branchAddress,
			accountType: Number(accountType),
			theseServicesAreFor: Number(theseServicesAreFor),
			bankManagerName: bankManagerName,
			bankManagerEmail: bankManagerEmail,
			bankManagerPhone: bankManagerPhone,
			bankManagerExt: bankManagerExt,
			isJointAccount: Number(isJointAccount),
			accountHolderName: accountHolderName,
			accountHolderEmail: accountHolderEmail,
			accountHolderPhone: accountHolderPhone,
			accountHolderExt: accountHolderExt,
			jointAccountHolderName: jointAccountHolderName,
			jointAccountHolderEmail: jointAccountHolderEmail,
			jointAccountHolderPhone: jointAccountHolderPhone,
			jointAccountHolderExt: jointAccountHolderExt,
		};
		(async () => {
			try {
				setIsPendingData(true)
				const res = await AuthService.savePADAgreenment(payload);
				if (res.status === 200 || res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
				scrollPanelFieldsToTop();

			} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
		})();
		// savePADAgreenmentFieldsData(payload);
	};

	const onBackToAccountSetupButtonClickHandler = () => {

		if (lastBankScreen && lastBankScreen !== 300) {
			(async () => {
				try {
					setIsPendingData(true)
					const response = await AuthService.lastBankScreen(userIdStore, 500);  //Go Back to account setup
					dispatch(
						signInUpdate({
							user: { lastBankScreen: 500 },
						})
					);
				} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
			})();
		}
		// alert("Coming Soon...")
	}
	const uploadIdsButtonClickHandler = () => {
		if (lastBankScreen && lastBankScreen !== 300) {
			(async () => {
				try {
					setIsPendingData(true)
					const response = await AuthService.lastBankScreen(userIdStore, 200);  //Upload documents
					dispatch(
						signInUpdate({
							user: { lastBankScreen: 200 },
						})
					);
				} catch { setIsPendingData(false) } finally { setIsPendingData(false) }
			})();
		}
	}
	const onPauseButtonClickHandler = () => {
		alert("Coming Soon...")
	}

	const downloadButtonClickHandler = () => {
		alert("Coming Soon...")
	}

	const onYesButtonClickedHanlder = () => {
		formMethods.handleSubmit(onSubmit)();
	}

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	return (
		<Grid>
			<BackdropCircularProgress
				open={isSubmitting || isPendingData || isPending}
				testId="yooo"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Bank"
										testId={`bank`}
										variant="outlined"
										topPadding={2}
										borderTop={true}
										borderLeft={true}
										borderRight={true}
										borderHide={true}
										showTitle={false}

									>
										<PADAgreenmentAccountDetailsPanel accountantBankDetailsForPADData={accountantBankDetailsForPADData} />
									</StandardPanel>
								}
								toolbar={
									<></>
								}
								showFullDiaplay={true}
								showToolbar={false}
								testId="form-layout"
							/>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Bank"
										testId={`bank`}
										variant="outlined"
										borderTop={true}
										borderLeft={true}
										borderRight={true}
										borderHide={true}

									>
										<PADAgreenmentBankFieldsPanel accountantBankDetailsForPADData={accountantBankDetailsForPADData} />
									</StandardPanel>
								}
								toolbar={
									<></>
								}
								showFullDiaplay={true}
								showToolbar={false}
								testId="form-layout"
							/>
							<TwoColumnLayout
								toolbar={
									<></>
								}
								leftColumnContent={
									<>
										<StandardPanel
											title="Bank Account Settings"
											testId={`bank-account-settings`}
											variant="outlined"
											borderHide={true}
											borderBottom={true}
										>
											<PADAgreenmentBankAccountSettingsFieldsPanel />
										</StandardPanel>
										<StandardPanel
											title="Account Holder Details"
											testId={`account-holder-details`}
											variant="outlined"
											borderHide={true}
										>
											<PADAgreenmentAccountHolderDetailsFieldsPanel setIsJointAccount={(value: number) => setIsJointAccount(value)} />
										</StandardPanel>
									</>
								}
								rightColumnContent={
									<>
										<StandardPanel
											title="Bank Manager"
											testId={`bank-manager`}
											variant="outlined"
											borderHide={true}
											borderBottom={Number(isJointAccount) === 200 ? true : false}
										>
											<PADAgreenmentBankManagerFieldsPanel />
										</StandardPanel>
										{Number(isJointAccount) === 200 ? <StandardPanel
											title="Joint Account Holder"
											testId={`joint-account-holder`}
											variant="outlined"
											borderHide={true}
										>
											<PADAgreenmentJointAccountHolderFieldsPanel />
										</StandardPanel> : <></>}
									</>
								}
								testId="t4-slip-setup-form"
								showToolbar={false}
							/>
							<PADAgreenmentToolbar
								isSubmitting={false}
								onSaveButtonClickHandler={onSaveButtonClickHandler}
								uploadIdsButtonClickHandler={uploadIdsButtonClickHandler}
								onBackToAccountSetupButtonClickHandler={onBackToAccountSetupButtonClickHandler}
								accountantBankDetailsForPADData={accountantBankDetailsForPADData}
								onPauseButtonClickHandler={onPauseButtonClickHandler}
								downloadButtonClickHandler={downloadButtonClickHandler}
							/>
						</Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedDialog
				dialogTitle="Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseHandler}
				open={openDialog}
				dialogContent={
					<>
						<Grid container spacing={0.5} sx={{ mt: 1 }}>
							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: ".875rem",
										color: "#555",
										lineHeight: 1.8,
									}}
								>
									Pressing Yes will:
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: ".875rem",
										color: "#666",
										lineHeight: 1.8,
										pl: 1,
									}}
								>
									- Email PAD to Account Holder(s)
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: ".875rem",
										color: "#666",
										lineHeight: 1.8,
										pl: 1,
									}}
								>
									- If not received, please check spam/junk folder
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: ".875rem",
										color: "#666",
										lineHeight: 1.8,
										pl: 1,
									}}
								>
									- PAD will be sent to the bank after signature(s)
								</Typography>
							</Grid>

							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Typography
									variant="body2"
									sx={{
										fontSize: ".875rem",
										color: "#666",
										lineHeight: 1.8,
										pl: 1,
									}}
								>
									- Follow up with your bank manager after signing the PAD
								</Typography>
							</Grid>
						</Grid>
					</>
				}
				showdividers={false}
				dialogAction={
					< Grid >
						<EnhancedButtonPrimeReact
							label="No"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Yes"
							// loading={isSubmitting}
							// type="submit"
							raised
							severity={EnhancedButtonPrimeReactSeverity.Success}
							style={{ marginLeft: "1rem" }}
							onClickHandler={onYesButtonClickedHanlder}
						/>
					</Grid >
				}
			/>

			< EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"security-questions-snackbar"}
				open={snackbarData.open}
			/>
		</Grid >
	);
};
